import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Badge,
  Popover,
  Typography,
  List,
  ListItemButton,
  Link,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  Chat as ChatIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Payments as PaymentsIcon,
  UploadFile as UploadFileIcon,
  Draw as DrawIcon,
  Article as ArticleIcon,
  Error as ErrorIcon,
  AlternateEmail as AlternateEmailIcon,
} from '@mui/icons-material';
import { enhancedApi } from '../api';

const POLL_INTERVAL = 30000;

const TYPE_CONFIG = {
  new_message: { Icon: ChatIcon, color: 'info.main', iconColor: 'info.contrastText' },
  task_assigned: { Icon: AssignmentIcon, color: 'warning.main', iconColor: 'warning.contrastText' },
  task_overdue: { Icon: WarningIcon, color: 'error.main' },
  invoice_paid: { Icon: PaymentsIcon, color: 'success.main' },
  document_uploaded: { Icon: UploadFileIcon, color: 'secondary.main' },
  esign_complete: { Icon: DrawIcon, color: 'success.main' },
  organizer_submitted: { Icon: ArticleIcon, color: 'info.main' },
  invoice_overdue: { Icon: ErrorIcon, color: 'error.main' },
  mention: { Icon: AlternateEmailIcon, color: 'warning.main' },
  assigned: { Icon: AssignmentIcon, color: 'warning.main' },
  approved: { Icon: PaymentsIcon, color: 'success.main' },
  rejected: { Icon: ErrorIcon, color: 'error.main' },
  overdue: { Icon: WarningIcon, color: 'error.main' },
  default: { Icon: NotificationsIcon, color: 'action.hover' },
};

const FILTER_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'messages', label: 'Messages' },
  { key: 'invoices', label: 'Invoices' },
];

const TASK_TYPES = ['task_assigned', 'task_overdue', 'assigned', 'approved', 'rejected', 'overdue'];
const MESSAGE_TYPES = ['new_message', 'mention'];
const INVOICE_TYPES = ['invoice_paid', 'invoice_overdue'];

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs > 1 ? 's' : ''} ago`;
  if (hrs < 48) return 'Yesterday';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const getTypeConfig = (type) => {
  const key = (type || '').toLowerCase();
  const cfg = TYPE_CONFIG[key] || TYPE_CONFIG.default;
  return { ...cfg, iconColor: cfg.color === 'action.hover' ? 'text.secondary' : 'background.paper' };
};

const getNavUrl = (n) => {
  if (n.task_instance_id) return `/tasks`;
  if (n.workflow_instance_id) return `/workflows`;
  const t = (n.type || n.notification_type || '').toLowerCase();
  if (t.includes('esign')) {
    const cid = n.client_id ?? n.clientId;
    if (cid != null) return `/client/${cid}`;
    return '/clients';
  }
  if (INVOICE_TYPES.some((x) => t.includes(x) || t.includes('invoice'))) return '/billing/invoices';
  if (MESSAGE_TYPES.some((x) => t.includes(x))) return '/inbox';
  if (TASK_TYPES.some((x) => t.includes(x))) return '/tasks';
  if (t.includes('organizer')) return '/tax-organizers';
  if (t.includes('document')) return '/clients';
  return '/notifications';
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterChip, setFilterChip] = useState('all');
  const [markingAll, setMarkingAll] = useState(false);
  const [justReceived, setJustReceived] = useState(false);
  const prevCountRef = useRef(0);
  const intervalRef = useRef(null);

  const open = Boolean(anchorEl);

  const fetchNotifications = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await enhancedApi.getNotifications();
      const data = res.data;
      const list = data?.notifications ?? data?.results ?? (Array.isArray(data) ? data : []);
      const count = data?.unread_count ?? list.filter((n) => !n.is_read).length;
      setNotifications(Array.isArray(list) ? list : []);
      setUnreadCount(count);
      if (count > prevCountRef.current && prevCountRef.current > 0) {
        setJustReceived(true);
        setTimeout(() => setJustReceived(false), 1500);
      }
      prevCountRef.current = count;
    } catch {
      if (!silent) setNotifications([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    intervalRef.current = setInterval(() => fetchNotifications(true), POLL_INTERVAL);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchNotifications]);

  const filteredNotifications = notifications.filter((n) => {
    if (filterChip === 'unread') return !n.is_read;
    const t = (n.type ?? n.notification_type ?? '').toLowerCase();
    if (filterChip === 'tasks') return TASK_TYPES.some((x) => t.includes(x));
    if (filterChip === 'messages') return MESSAGE_TYPES.some((x) => t.includes(x));
    if (filterChip === 'invoices') return INVOICE_TYPES.some((x) => t.includes(x));
    return true;
  });

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => setAnchorEl(null);

  const handleMarkOneRead = async (id) => {
    const n = notifications.find((x) => x.id === id);
    if (n && !n.is_read) {
      setNotifications((prev) => prev.map((x) => (x.id === id ? { ...x, is_read: true } : x)));
      setUnreadCount((c) => {
        const next = Math.max(0, c - 1);
        prevCountRef.current = next;
        return next;
      });
      try {
        await enhancedApi.markNotificationRead(id);
      } catch {
        setNotifications((prev) => prev.map((x) => (x.id === id ? { ...x, is_read: false } : x)));
        setUnreadCount((c) => {
          const next = c + 1;
          prevCountRef.current = next;
          return next;
        });
      }
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    const prevNotifications = notifications;
    const prevCount = unreadCount;
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
    prevCountRef.current = 0;
    try {
      await enhancedApi.markAllNotificationsRead();
    } catch {
      setNotifications(prevNotifications);
      setUnreadCount(prevCount);
      prevCountRef.current = prevCount;
    } finally {
      setMarkingAll(false);
    }
  };

  const handleNotificationClick = (n) => {
    if (!n.is_read) handleMarkOneRead(n.id);
    handleClose();
    navigate(getNavUrl(n));
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleOpen}
        size="medium"
        aria-label="Notifications"
        sx={{
          '@keyframes bellPulse': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.2)' },
          },
          '& .MuiBadge-badge': {
            ...(justReceived && { animation: 'bellPulse 0.6s ease-in-out 2' }),
          },
        }}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon fontSize="medium" />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 4,
          sx: {
            width: 380,
            maxWidth: '95vw',
            maxHeight: 520,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Link
                component="button"
                variant="body2"
                onClick={handleMarkAllRead}
                disabled={markingAll}
                sx={{ cursor: markingAll ? 'wait' : 'pointer', textDecoration: 'none' }}
              >
                {markingAll ? 'Marking...' : 'Mark all read'}
              </Link>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {FILTER_CHIPS.map((chip) => (
              <Chip
                key={chip.key}
                label={chip.label}
                size="small"
                onClick={() => setFilterChip(chip.key)}
                color={filterChip === chip.key ? 'primary' : 'default'}
                variant={filterChip === chip.key ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: 400 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filteredNotifications.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 5,
                gap: 1,
              }}
            >
              <NotificationsNoneIcon sx={{ fontSize: 48, color: 'action.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                You're all caught up!
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filteredNotifications.map((n) => {
                const cfg = getTypeConfig(n.type ?? n.notification_type);
                const Icon = cfg.Icon;
                const isUnread = !n.is_read;
                return (
                  <ListItemButton
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    sx={{
                      px: 2,
                      py: 1.25,
                      gap: 1.5,
                      alignItems: 'flex-start',
                      '&:hover': { bgcolor: 'action.hover' },
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        bgcolor: cfg.color,
                        color: cfg.iconColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Icon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: isUnread ? 600 : 400 }}
                      >
                        {n.title || n.message || 'Notification'}
                      </Typography>
                      {n.message && n.title && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                          {n.message}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {timeAgo(n.created_at)}
                      </Typography>
                    </Box>
                    {isUnread && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          flexShrink: 0,
                          mt: 1,
                        }}
                      />
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              handleClose();
              navigate('/notifications');
            }}
            sx={{ cursor: 'pointer', fontWeight: 500 }}
          >
            View all notifications
          </Link>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
